import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarRange } from "lucide-react";
import SectionHead from "./SectionHead";
import { sectionPad } from "./motion";
import { useLang } from "../../i18n/LanguageContext";

const PADDOCK_IMG = "/img/paddock.jpg";

export default function Programma() {
  const { t } = useLang();
  const c = t.programma;
  const [active, setActive] = useState(5);
  const days = c.days;

  return (
    <section id="programma" className={`relative bg-vg-card ${sectionPad}`}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead index="02" kicker={c.kicker} title={c.title} en={c.en} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-center gap-2"
          data-testid="programma-day-tabs"
        >
          <span className="mr-3 hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted sm:inline-flex">
            <CalendarRange className="h-4 w-4 text-vg-green" />
            {c.mayLabel}
          </span>
          {days.map((day, i) => (
            <button
              key={day.d}
              onClick={() => setActive(i)}
              data-testid={`programma-day-${day.d}`}
              aria-pressed={active === i}
              className={`relative flex min-w-[64px] flex-col items-center rounded-lg border px-4 py-3 transition-all duration-200 ${
                active === i
                  ? "border-vg-green bg-vg-green text-vg-black shadow-[0_0_24px_rgba(16,185,129,0.4)]"
                  : "border-white/10 bg-vg-black text-vg-muted hover:border-vg-green/50 hover:text-vg-white"
              }`}
            >
              <span className="font-display text-2xl font-bold leading-none">{day.d}</span>
              <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em]">{day.wd}</span>
              {day.hot && <span className={`absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full ${active === i ? "bg-vg-red" : "bg-vg-red/70"}`} aria-label="Giorno di gara" />}
            </button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active + "-" + t.programma.mayLabel}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 grid overflow-hidden border border-white/10 bg-vg-black lg:grid-cols-12"
            data-testid="programma-detail"
          >
            <div className="flex flex-col justify-center p-8 sm:p-12 lg:col-span-7">
              <div className="flex items-center gap-4">
                <span className="text-outline font-display text-7xl font-bold leading-none sm:text-8xl">
                  {days[active].d.padStart(2, "0")}
                </span>
                <span className={`hud-chip ${days[active].hot ? "border-vg-red/50 text-vg-red" : ""}`}>{days[active].tag}</span>
              </div>
              <h3 className="mt-6 font-display text-3xl font-bold uppercase tracking-wide text-vg-white sm:text-4xl">
                {days[active].phase}
              </h3>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-vg-muted">{days[active].desc}</p>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-vg-green">
                {days[active].wd} · {days[active].d} {c.mayLabel} — Misano World Circuit
              </p>
            </div>
            <div className="relative min-h-[220px] lg:col-span-5">
              <img src={PADDOCK_IMG} alt="Vita di box in paddock" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-vg-black via-vg-black/40 to-transparent" aria-hidden="true" />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
